import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, catchError, map, throwError } from 'rxjs';

import { environment } from '@fantaz/shared/app-config';
import { VERIFICATION_TYPES } from '@fantaz/shared/config';
import {
  Address,
  AuthEventsResponse,
  AuthUserEvents,
  CancelSubscriptionsResponse,
  DuplicateUsernameFailure,
  ITournamentReferralResponse,
  Invitation,
  Profile,
  ReferralResponse,
  Suggestion,
  TournamentsPlayedRequest,
  TournamentsPlayedResponse,
  User,
  UsernameBlackListFailure,
} from '@fantaz/shared/data-access/models';
import { getInterval } from '@fantaz/shared/utils/date';

import { VerifyUserExceptions } from './account';

interface ServiceCallResponse<T> {
  success: boolean;
  payload?: T;
  error?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileApiService {
  constructor(private http: HttpClient) {}

  fetchUserProfile(): Observable<Profile> {
    return this.http
      .get<Profile>(`${environment.apiBaseUrl}/core-client/api/user/profile`)
      .pipe(
        map((response) => ({
          ...response,
          ...(response?.legalAddress && {
            legalAddress: new Address(response?.legalAddress),
          }),
          ...(response?.shippingAddress && {
            shippingAddress: new Address(response?.shippingAddress),
          }),
        })),
      );
  }

  /**
   * Service to update the current authenticated user profile
   *
   * @param data Partial<Profile>
   * @returns Observer<ServiceCallResponse<User>>
   */
  updateUserProfile(
    data: Partial<Profile>,
  ): Observable<ServiceCallResponse<User>> {
    return this.http
      .put<User | { status: string; profileMatch: any }>(
        `${environment.apiBaseUrl}/core-client/api/user/profile`,
        {
          ...data,
          ...(data.shippingAddress
            ? {
                shippingAddress: {
                  ...data.shippingAddress,
                  country: 'US', // for now always send US
                },
              }
            : {}),
          ...(data.legalAddress
            ? {
                legalAddress: {
                  ...data.legalAddress,
                  country: 'US', // for now always send US
                },
              }
            : {}),
        },
      )
      .pipe(
        map((response) => {
          if ('status' in response) {
            if (response.status === VERIFICATION_TYPES.DUPLICATE) {
              throw VerifyUserExceptions.duplicated(response?.profileMatch);
            }
          }

          return {
            success: true,
            payload: {
              ...response,
              ...Object.assign({}, data, {
                entity: undefined,
              }),
            },
          } as ServiceCallResponse<User>;
        }),
        catchError((error: any) => {
          if (error?.error?.message === 'USERNAME_TAKEN') {
            return throwError(() => new DuplicateUsernameFailure());
          }

          if (error?.error?.message === 'USERNAME_BLACK_LISTED') {
            return throwError(() => new UsernameBlackListFailure());
          }

          return throwError(() => error);
        }),
      );
  }

  fetchAllInvitations(
    options: {
      pageIndex: number;
      pageSize: number;
    } = {
      pageIndex: 0,
      pageSize: 10,
    },
  ): Observable<{ invites: Invitation[]; total: number }> {
    return this.http.get<{ invites: Invitation[]; total: number }>(
      `${environment.apiBaseUrl}/core/client/api/member/invitation?pageIndex=${options.pageIndex}&pageSize=${options.pageSize}`,
    );
  }

  fetchAllSuggestions(): Observable<{ suggestions: Suggestion[] }> {
    return this.http
      .get<{
        suggestions: Suggestion[];
      }>(`${environment.apiBaseUrl}/core-client/api/user/suggestions`)
      .pipe(
        map((response) => {
          // override actions
          const actions: { [key: string]: string } = {
            '/promote': '#promote',
          };
          return {
            suggestions: response.suggestions.map((suggestion) => {
              return {
                ...suggestion,
                ...(actions[suggestion.action as keyof typeof actions]
                  ? {
                      action:
                        actions[suggestion.action as keyof typeof actions],
                    }
                  : {}),
              };
            }),
          };
        }),
      );
  }

  getActiveSubscriptions(): Observable<any> {
    return this.http.get<any>(
      `${environment.apiBaseUrl}/core/client/api/getActiveSubscriptions`,
    );
  }

  subscribeZbo(options: { uuid: string }): Observable<any> {
    return this.http.get<any>(
      `${environment.apiBaseUrl}/core/client/api/membership/pay?subscriptionUuid=${options.uuid}`,
    );
  }

  cancelSubscription(): Observable<CancelSubscriptionsResponse> {
    return this.http.get<CancelSubscriptionsResponse>(
      `${environment.apiBaseUrl}/core/client/api/membership/cancel`,
    );
  }

  fetchReferralInfo(options: {
    startDate?: string;
    endDate?: string;
    pageIndex: number;
    pageSize: number;
    tournament?: string;
    tournamentNames?: string[];
  }): Observable<ReferralResponse> {
    const httpParams = new HttpParams({ fromObject: options });
    return this.http.get<ReferralResponse>(
      `${
        environment.apiBaseUrl
      }/core-client/referrals-info?${httpParams.toString()}`,
    );
  }

  fetchTournamentsByReferralInfo(options: {
    startDate?: string;
    endDate?: string;
  }): Observable<ITournamentReferralResponse> {
    const httpParams = new HttpParams({ fromObject: options });
    return this.http.get<ITournamentReferralResponse>(
      `${
        environment.apiBaseUrl
      }/core-client/api/user/tournaments/referral-info?${httpParams.toString()}`,
    );
  }

  /**
   * Service to check if username exist
   * @param options
   * @returns Observable<boolean> true if username exist and false if not
   */
  checkUserName(options: { username: string }): Observable<boolean> {
    return this.http
      .get<{
        exists: boolean;
      }>(`${environment.apiBaseUrl}/core/api/user/${options.username}/check`)
      .pipe(map((res) => res.exists));
  }

  /**
   * This method retrieves authentication events for a user.
   * It makes a GET request to the '/core-client/api/user/auth-events' endpoint.
   * The response is an array of authentication events, each with a 'CreationDate' property.
   * For each event, it calculates the interval between the 'CreationDate' and the current date/time.
   * This interval is then added to the event object as the 'LastAuthenticatedInterval' property.
   * The method returns an Observable of the modified array of events.
   *
   * @returns {Observable<AuthUserEvents[]>} - An Observable of an array of authentication events, each with a 'LastAuthenticatedInterval' property.
   */
  getAuthEvents(): Observable<AuthUserEvents[]> {
    return this.http
      .get<AuthEventsResponse>(
        `${environment.apiBaseUrl}/core-client/api/user/auth-events`,
      )
      .pipe(
        map((res) => {
          const result = res.AuthEvents.map((event) => {
            const lastAuthenticatedDate = new Date(event.CreationDate);
            const interval = getInterval({
              start: lastAuthenticatedDate,
              end: new Date(),
            });
            event.LastAuthenticatedInterval = interval;
            return event;
          });
          return result;
        }),
      );
  }

  /**
   * This method updates the user's contact attributes.
   * It makes a POST request to the '/core-client/api/user/update-contact' endpoint.
   * The request body is an object that may contain 'email' and 'phone_number' properties.
   * The method returns an Observable of an object with a 'message' property.
   *
   * @param {Object} args - An object that may contain 'email' and 'phone_number' properties.
   * @returns {Observable<{ message: string }>} - An Observable of an object with a 'message' property.
   */
  updateUserContactAttribute(args: {
    email?: string;
    phone_number?: string;
  }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiBaseUrl}/core-client/api/user/update-contact`,
      { ...args },
    );
  }

  handleError(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    console.log(errorMessage);
    return throwError(() => {
      return errorMessage;
    });
  }

  getUserTournamentsPlayed(props: TournamentsPlayedRequest) {
    // Use HttpParams to build query parameters cleanly
    let params = new HttpParams().set('userId', props.userId);

    if (props.startAt) {
      params = params.set('startAt', props.startAt);
    }

    if (props.endAt) {
      params = params.set('endAt', props.endAt);
    }

    return this.http.get<TournamentsPlayedResponse>(
      `${environment.apiBaseUrl}/api/user/tournaments/played`,
      { params },
    );
  }
}
